import React, {useEffect, useState, useRef, useCallback} from "react";
import { chatService } from "api/chat-service";
import { Confirm, Button, useTranslate } from "react-admin";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import "./style.css";
import inMemoryJwt from "Application/Auth/inMemoryJwt";
import {VoiceRecorder} from "../../../Component/Layouts/AudioRecorder/AudioRecorder";
import authService from "../../../api/auth-service";
import {PetInfoPanel} from "./petInfoPanel";

import { useReply } from "./Chat/Hooks/useReply";
import { useSwipeReply } from "./Chat/Hooks/useSwipeReply";
import { ContextMenu } from "./Chat/Components/contextMenu";
import { ReplyPreview } from "./Chat/Components/replyPreview";
import { ChatMessageList } from "./Chat/Components/chatMessageList";
import { useChatViewportRefs } from "./Chat/Hooks/useChatViewportRefs";
import { useChatThread } from "./Chat/Hooks/useChatThread";
import { useChatScrollPagination } from "./Chat/Hooks/useChatScrollPagination";
import { useChatReadState } from "./Chat/Hooks/useChatReadState";
import { useChatComposer } from "./Chat/Hooks/useChatComposer";

export const ChatPage = () => {
  const { id, userDoctorType } = useParams();
  const [user, setUser] = useState(null);
  const [chatId, setChatId] = useState(null);
  const t = useTranslate();
  const [previewImage, setPreviewImage] = useState(null);

  const [isEditPetDescription, setIsEditPetDescription] = useState(false);
  const [isLoadingEditPet, setIsLoadingEditPet] = useState(false);

  const [petDescription, setPetDescription] = useState("");
  const [prevPetDescription, setPrevPetDescription] = useState("");

  const [prescription, setPrescription] = useState("");
  const [prevPrescription, setPrevPrescription] = useState("");

  const [dateFrom, setDateFrom] = useState(null);
  const [prevDateFrom, setPrevDateFrom] = useState(null);

  const [dateTo, setDateTo] = useState(null);
  const [prevDateTo, setPrevDateTo] = useState(null);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleClickVariant = useCallback(
      (message, variant) => {
        enqueueSnackbar(message, {
          variant,
          anchorOrigin: { vertical: "top", horizontal: "center" },
          action: (snackbarId) => (
              <button
                  onClick={() => closeSnackbar(snackbarId)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "white",
                    cursor: "pointer",
                    fontSize: "18px",
                    lineHeight: 1,
                    padding: "0 4px",
                    alignSelf: "flex-start",
                    marginTop: "10px",
                  }}
              >
                ✕
              </button>
          ),
        });
      },
      [closeSnackbar, enqueueSnackbar]
  );

  const handleError = useCallback(
      (error) => {
        const responseData = error?.response?.data;
        const dataMessages = Array.isArray(responseData?.data)
            ? responseData.data.map((err) => err.message).filter(Boolean).join(", ")
            : null;
        const backendMessage = [
          responseData?.message || error?.message || "Something went wrong",
          dataMessages,
        ]
            .filter(Boolean)
            .join(": ");
        handleClickVariant(backendMessage, "error");
      },
      [handleClickVariant]
  );

  const viewport = useChatViewportRefs();
  const {
    sendButtonRef,
    hiddenFileInput,
    messagesEndRef,
    scrollContainerRef,
    topSentinelRef,
    bottomSentinelRef,
    shouldAutoScrollRef,
    initialScrollPendingRef,
    inFlightRef,
    readStateRef,
    lastSeenIncomingMessageIdRef,
  } = viewport;

  const thread = useChatThread(chatId, handleError, viewport);
  const scroll = useChatScrollPagination({
    viewport,
    isFetchingInitial: thread.isFetchingInitial,
    isFetchingOlder: thread.isFetchingOlder,
    isFetchingNewer: thread.isFetchingNewer,
    runInitialMessagesFetch: thread.runInitialMessagesFetch,
  });
  const {
    messages,
    setMessages,
    pagination,
    isFetchingInitial,
    isFetchingOlder,
    isPolling,
    isJumpingToMessage,
    refreshLatestMessages,
    pollMessagesPage,
    handleJumpToMessage,
    resetChatTranscript,
  } = thread;
  const {
    scrollToBottom,
    loadInitialMessages,
    resetViewportForNewChatId,
  } = scroll;

  const currentLoginUserId = inMemoryJwt.getId();
  const navigate = useNavigate();
  const doctorType = inMemoryJwt.getUserType();

  const [openDeleteMessageConfirm, setOpenDeleteMessageConfirm] = useState(null);

  const audioRefs = useRef({});
  const [audioSpeed, setAudioSpeed] = useState(1);

  const { replyingTo, startReply, cancelReply } = useReply();

  const { handleMarkChatIsRead } = useChatReadState({
    chatId,
    messages,
    user,
    currentLoginUserId,
    doctorType,
    readStateRef,
    lastSeenIncomingMessageIdRef,
    pollMessagesPage,
    shouldAutoScrollRef,
    scrollToBottom,
  });

  const composer = useChatComposer({
    chatId,
    userId: currentLoginUserId,
    sendButtonRef,
    hiddenFileInput,
    shouldAutoScrollRef,
    handleError,
    handleClickVariant,
    refreshLatestMessages,
    scrollToBottom,
    handleMarkChatIsRead,
    cancelReply,
    replyingTo,
  });

  const {
    newMessage,
    setNewMessage,
    isLoadingFile,
    file,
    audioFile,
    setAudioFile,
    isRecording,
    setIsRecording,
    sendButtonWidth,
    isLoading,
    editingMessage,
    handleStartEdit,
    handleCancelEdit,
    handleSendMessage,
    handleChangeFile,
    renderFileLabel,
    openFilePicker,
    clearAttachedFile,
  } = composer;

  const [contextMenu, setContextMenu] = useState(null);

  const getUserName = useCallback(
      (msg) => {
        if (!msg) return "";
        return msg.sender === "doctor"
            ? `${msg?.doctor?.firstName ?? ""} ${msg?.doctor?.lastName ?? ""} (doctor)`
            : (user?.name ?? "") + " (client)";
      },
      [user]
  );

  const { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel } = useSwipeReply(
      useCallback(
          (msg) => {
            startReply(msg);
          },
          [startReply]
      )
  );

  const handleContextMenu = useCallback((e, msg) => {
    if (window.innerWidth > 767) {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY, msg });
    }
  }, []);

  const buildContextMenuItems = useCallback(
      (msg) => {
        if (!msg) return [];
        const items = [];

        items.push({
          label: "Reply",
          icon: "↩",
          onClick: () => startReply(msg),
        });

        return items;
      },
      [startReply]
  );

  useEffect(() => {
    Object.values(audioRefs.current).forEach((audio) => {
      if (audio) audio.playbackRate = audioSpeed;
    });
  }, [audioSpeed]);

  const handleGetUser = useCallback(async () => {
    try {
      const response = await chatService.getUser(id);
      setUser(response);

      if (userDoctorType === "private" && response?.chat?.id) {
        setChatId(response?.chat?.id);
      } else if (userDoctorType === "public" && response?.publicChat?.id) {
        setChatId(response?.publicChat?.id);
      } else {
        setChatId(null);
      }

      if (response.pets.length) {
        const pet = response.pets[0];
        setPetDescription(pet.doctorDescription ?? "");
        setPrevPetDescription(pet.doctorDescription ?? "");
        setPrescription(pet.prescription ?? "");
        setPrevPrescription(pet.prescription ?? "");
        const from = pet.dateFrom ?? null;
        const to = pet.dateTo ?? null;
        setDateFrom(from);
        setPrevDateFrom(from);
        setDateTo(to);
        setPrevDateTo(to);
      }
    } catch (error) {
      handleError(error);
    }
  }, [handleError, id, userDoctorType]);

  const handleUpdatePetDescription = async () => {
    if (!user?.pets?.length || isLoadingEditPet) return;
    setIsLoadingEditPet(true);
    try {
      await chatService.updatePetDescription(user.pets[0].id, {
        doctorDescription: petDescription,
        prescription: prescription,
        dateFrom: dateFrom,
        dateTo: dateTo,
      });
      setPrevPetDescription(petDescription);
      setPrevPrescription(prescription);
      setPrevDateFrom(dateFrom);
      setPrevDateTo(dateTo);
      setIsEditPetDescription(false);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoadingEditPet(false);
    }
  };

  const handleCancelPetDescription = () => {
    if (user?.pets?.length) {
      setPetDescription(prevPetDescription);
      setPrescription(prevPrescription);
      setDateFrom(prevDateFrom);
      setDateTo(prevDateTo);
    } else {
      setPetDescription("");
      setPrescription("");
      setDateFrom(null);
      setDateTo(null);
    }
    setIsEditPetDescription(false);
  };

  const handleGoBackToPatients = async () => {
    let urlPath = window.location.hash.split("doctorId=");
    if (urlPath[1] && userDoctorType) {
      navigate(`/admins/${urlPath[1]}/show/${userDoctorType}-chats`);
    }
    const myUser = await authService.getMe();
    if (myUser && myUser.role === "ROLE_ADMIN") {
      if (userDoctorType === "private") {
        navigate(`/admins/${user.chat.lastDoctorMessage.doctor.id}/show/private-chats`);
      } else if (userDoctorType === "public") {
        navigate(`/admins/${user.publicChat.lastDoctorMessage.doctor.id}/show/public-chats`);
      } else {
        navigate(-1);
      }
      return;
    }
    if (myUser?.id) {
      if (userDoctorType === "private") {
        navigate(`/admins/${myUser.id}/show/private-chats`);
      } else if (userDoctorType === "public") {
        navigate(`/admins/${myUser.id}/show/public-chats`);
      }
    } else {
      navigate(-1);
    }
  };

  useEffect(() => { handleGetUser(); }, [handleGetUser]);

  useEffect(() => {
    if (!chatId) return;
    resetViewportForNewChatId();
    resetChatTranscript();
    loadInitialMessages().then((isLoaded) => {
      if (isLoaded) {
        handleMarkChatIsRead();
      }
    });
  }, [chatId, handleMarkChatIsRead, loadInitialMessages, resetChatTranscript, resetViewportForNewChatId]);

  const getDate = (date) => {
    const d = new Date(date);
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: false,
    });
  };

  const onConfirmDeleteMessage = async (id) => {
    if (!id) return;
    try {
      await chatService.deleteMessage(chatId, id);
      setMessages((prev) => prev.filter((message) => message?.id !== id));
      await refreshLatestMessages();
    } catch (error) {
      handleError(error);
    }
  };

  const getTopHistoryStatus = useCallback(() => {
    if (isFetchingInitial || messages.length === 0) return null;
    if (isFetchingOlder || inFlightRef.current.older) {
      return t("resources.chat.loadingOlderMessages");
    }
    if (pagination.hasPrevPage) return t("resources.chat.moreMessagesAbove");
    return t("resources.chat.noMoreMessagesAbove");
  }, [inFlightRef, isFetchingInitial, isFetchingOlder, messages.length, pagination.hasPrevPage, t]);

  return (
      <>
        <div className="chat-page-wrapper">
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
            <PetInfoPanel
                user={user}
                userDoctorType={userDoctorType}
                petDescription={petDescription}
                setPetDescription={setPetDescription}
                prescription={prescription}
                setPrescription={setPrescription}
                dateFrom={dateFrom}
                setDateFrom={setDateFrom}
                dateTo={dateTo}
                setDateTo={setDateTo}
                isEditMode={isEditPetDescription}
                setIsEditMode={setIsEditPetDescription}
                onSave={handleUpdatePetDescription}
                onCancel={handleCancelPetDescription}
                hasPets={Boolean(user?.pets?.length)}
                handleGoBackToPatients={handleGoBackToPatients}
                getDate={getDate}
            />
          </div>

          <ChatMessageList
              scrollContainerRef={scrollContainerRef}
              initialScrollPendingRef={initialScrollPendingRef}
              scrollToBottom={scrollToBottom}
              isFetchingInitial={isFetchingInitial}
              messages={messages}
              topSentinelRef={topSentinelRef}
              isJumpingToMessage={isJumpingToMessage}
              t={t}
              getTopHistoryStatus={getTopHistoryStatus}
              bottomSentinelRef={bottomSentinelRef}
              messagesEndRef={messagesEndRef}
              isPolling={isPolling}
              currentLoginUserId={currentLoginUserId}
              doctorType={doctorType}
              user={user}
              getUserName={getUserName}
              handleJumpToMessage={handleJumpToMessage}
              handleContextMenu={handleContextMenu}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onTouchCancel={onTouchCancel}
              onStartEditMessage={handleStartEdit}
              onRequestDeleteMessage={setOpenDeleteMessageConfirm}
              getDate={getDate}
              audioRefs={audioRefs}
              audioSpeed={audioSpeed}
              onAudioSpeedChange={setAudioSpeed}
              setPreviewImage={setPreviewImage}
          />

          {doctorType ? (
              <div className="wrapper-send">
                <div className="upload-file">
                  {isLoadingFile ? (
                      <div>Loading...</div>
                  ) : (
                      file && <div className="file-name">{renderFileLabel(file.type)}</div>
                  )}
                  {file && (
                      <div>
                        <Button onClick={openFilePicker} variant="text" size="icon" className="button-text-edit">
                          <img src={process.env.PUBLIC_URL + "/edit.svg"} alt="Upload" />
                          {t("resources.chat.edit")}
                        </Button>
                        <Button className="button-text-delete" onClick={clearAttachedFile} variant="text" size="icon">
                          <img src={process.env.PUBLIC_URL + "/delete.svg"} alt="Upload" />
                          {t("resources.chat.remove")}
                        </Button>
                      </div>
                  )}
                  {!file && (
                      <Button size="icon" variant="contained" className="button-primary" onClick={openFilePicker}>
                        <img
                            src={process.env.PUBLIC_URL + "/upload.svg"}
                            alt="Upload"
                            style={{ width: "20px", height: "20px" }}
                        />
                        {t("resources.chat.sendFile")}
                      </Button>
                  )}
                  <input
                      accept="image/*, .pdf, .docx, video/*, audio/*"
                      type="file"
                      style={{ display: "none" }}
                      onChange={(event) => handleChangeFile(event)}
                      ref={hiddenFileInput}
                  />
                </div>

                {isLoading ? (
                    <div>Loading...</div>
                ) : (
                    <div className="textarea-block">
                      {replyingTo && !editingMessage && (
                          <ReplyPreview
                              message={replyingTo}
                              senderName={getUserName(replyingTo)}
                              onCancel={cancelReply}
                          />
                      )}

                      {editingMessage && (
                          <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "4px 8px",
                                background: "#e8f0fe",
                                borderRadius: 4,
                                marginBottom: 4,
                              }}
                          >
                    <span style={{ fontSize: 12, color: "#555" }}>
                      {t("resources.chat.editMessageTitle")}
                    </span>
                            <span
                                onClick={handleCancelEdit}
                                style={{ cursor: "pointer", fontSize: 12, color: "#d32f2f" }}
                            >
                      ✕ {t("resources.chat.editMessageCancelButton")}
                    </span>
                          </div>
                      )}

                      {newMessage.trim().length > 0 && (
                          <div className="textarea-clear-wrap">
                            <Button
                                variant="text"
                                size="small"
                                className="button-text-clear-message"
                                onClick={() => setNewMessage("")}
                            >
                              {t("resources.chat.clearInput")}
                            </Button>
                          </div>
                      )}

                      <textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          disabled={Boolean(audioFile)}
                          placeholder={audioFile ? "" : t("resources.chat.inputPlaceholder")}
                          className="textarea"
                      />
                      <div
                          style={{
                            left: Boolean(audioFile) ? "10px" : "unset",
                            right: Boolean(audioFile) ? "unset" : `${sendButtonWidth + 10}px`,
                          }}
                          className="voice-recorder-wrap"
                      >
                        <VoiceRecorder
                            audioFile={audioFile}
                            passAudio={(e) => {
                              setAudioFile(e);
                              setNewMessage("");
                            }}
                            passIsRecording={(e) => setIsRecording(e)}
                        />
                      </div>
                      <div ref={sendButtonRef} className="send-button-wrap">
                        <Button
                            disabled={isRecording}
                            variant="contained"
                            className="button-send"
                            onClick={handleSendMessage}
                        >
                          <img src={`${process.env.PUBLIC_URL}/send.svg`} alt="Send" />
                        </Button>
                      </div>
                    </div>
                )}
              </div>
          ) : null}
        </div>

        {contextMenu && buildContextMenuItems(contextMenu.msg).length > 0 && (
            <ContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                items={buildContextMenuItems(contextMenu.msg)}
                onClose={() => setContextMenu(null)}
            />
        )}

        {openDeleteMessageConfirm && (
            <Confirm
                isOpen={openDeleteMessageConfirm}
                title="Confirm delete"
                content="Are you sure you want to delete this message?"
                onConfirm={() => {
                  onConfirmDeleteMessage(openDeleteMessageConfirm);
                  setOpenDeleteMessageConfirm(null);
                }}
                onClose={() => setOpenDeleteMessageConfirm(null)}
            />
        )}

        {previewImage && (
            <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(0, 0, 0, 0.7)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1200,
                }}
                onClick={() => setPreviewImage(null)}
            >
              <img
                  src={previewImage}
                  alt="Full Preview"
                  style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: "8px" }}
              />
            </div>
        )}
      </>
  );
};